import React, { useCallback, useMemo } from 'react';
import { ResourceType } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import {
  useArtifactPanel,
  type ArtifactPanelViewEntry,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { WorkflowRunStackContent } from '../../workflow/workflow-run-detail.page';
import { isWorkflowRunStepViewPayload } from '../../workflow/workflow-artifact-views';
import { runDetailsCrumbLabel } from '../../workflow/workflow-breadcrumbs';
import { LibraryContentsPageArtifactView } from '../../folder/folder-contents.page';
import {
  ResourcePageShell,
  ResourcePageShellBackButton,
} from '../resource-page-shell';
import { useWhoami } from '../../../stores/org';
import { resourceItemPath } from './resource-navigator';

/**
 * The closed set of destinations an artifact panel can show in place rather than
 * navigating away from. Each entry pairs a full-page route (the anchor's `href`,
 * and where the click lands on a standalone page) with the view the panel pushes
 * onto the focused artifact's stack.
 *
 * Adding a destination means adding a key here and a case to
 * {@link resolveArtifactLink} — the compiler enforces both. `<ArtifactLink>`'s
 * `to` prop accepts nothing else, so a link can't point somewhere the panel has
 * no way to render.
 */
export type ArtifactLinkParams = {
  'workflow-run': { workflowId: string; runId: string };
  /** The Library (root folder view). It has no id, so it carries no params. */
  library: Record<never, never>;
};

export type ArtifactLinkKind = keyof ArtifactLinkParams;

/** A destination for `<ArtifactLink to={...}>`, discriminated by `kind`. */
export type ArtifactLinkTarget = {
  [K in ArtifactLinkKind]: { kind: K } & ArtifactLinkParams[K];
}[ArtifactLinkKind];

export const ARTIFACT_LINK_VIEW_TYPE = 'artifact-link' as const;

/**
 * Payload of a stack entry pushed by an `<ArtifactLink>`. Tagged so the panel can
 * tell these apart from the view types an artifact pushes onto its own stack
 * (workflow drill-ins, run steps, …), which the artifact renders for itself.
 */
export type ArtifactLinkViewPayload = {
  type: typeof ARTIFACT_LINK_VIEW_TYPE;
  link: ArtifactLinkTarget;
};

const isArtifactLinkTarget = (value: unknown): value is ArtifactLinkTarget => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  switch (record['kind']) {
    case 'workflow-run':
      return (
        typeof record['workflowId'] === 'string' &&
        typeof record['runId'] === 'string'
      );
    case 'library':
      return true;
    default:
      return false;
  }
};

export const isArtifactLinkViewPayload = (
  payload: unknown
): payload is ArtifactLinkViewPayload => {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === ARTIFACT_LINK_VIEW_TYPE &&
    isArtifactLinkTarget(record['link'])
  );
};

/**
 * `resourceItemPath` only returns null for resource types with no item route, and
 * workflows have one — the fallback keeps this total without a non-null assertion.
 */
const workflowItemPath = (orgSlug: string, workflowId: string): string =>
  resourceItemPath(orgSlug, ResourceType.Workflow, workflowId) ??
  `/${orgSlug}/workflows/${workflowId}`;

export type ResolvedArtifactLink = {
  /** Full-page route: the anchor's `href`, and where a plain click lands off-panel. */
  href: string;
  /** Stack entry pushed when the click happens inside an artifact panel. */
  entry: ArtifactPanelViewEntry;
  /**
   * Body shown in place of the host artifact while this link is the deepest one
   * on the stack. A thunk rather than an element so callers that only need the
   * `href` don't build one; the element type is module-level, so re-invoking it
   * on each render reconciles instead of remounting.
   *
   * `nested` is everything pushed *above* this link — drill-ins raised by the
   * link's own body (a run graph's step view, …), which share the host
   * artifact's one stack. The destination renders them itself; anything it
   * doesn't recognise it ignores, so a drill-in can never fall through to the
   * host artifact.
   */
  render: (nested: readonly ArtifactPanelViewEntry[]) => React.ReactNode;
};

export const resolveArtifactLink = (
  link: ArtifactLinkTarget,
  orgSlug: string
): ResolvedArtifactLink => {
  switch (link.kind) {
    case 'workflow-run': {
      const { workflowId, runId } = link;
      return {
        href: `${workflowItemPath(orgSlug, workflowId)}/runs/${runId}`,
        entry: {
          id: `${ARTIFACT_LINK_VIEW_TYPE}:workflow-run:${workflowId}:${runId}`,
          title: runDetailsCrumbLabel(runId),
          payload: { type: ARTIFACT_LINK_VIEW_TYPE, link },
        },
        render: (nested) => (
          <WorkflowRunLinkView
            workflowId={workflowId}
            runId={runId}
            nested={nested}
          />
        ),
      };
    }
    case 'library': {
      return {
        href: `/${orgSlug}/folders`,
        entry: {
          id: `${ARTIFACT_LINK_VIEW_TYPE}:library`,
          title: 'Library',
          payload: { type: ARTIFACT_LINK_VIEW_TYPE, link },
        },
        render: () => <LibraryContentsPageArtifactView />,
      };
    }
    default:
      return Assert.unreachable(link);
  }
};

/**
 * A run on someone else's stack. Opening a sibling run from inside it (a retry,
 * the run that superseded this one) replaces the top entry instead of stacking,
 * so Back returns to the artifact rather than walking a chain of runs.
 *
 * Clicking a node in the run graph pushes a step view onto that same shared
 * stack, so this view renders it — otherwise the panel would see a top entry it
 * doesn't own and drop back to the host artifact.
 */
const WorkflowRunLinkView: React.FC<{
  workflowId: string;
  runId: string;
  nested: readonly ArtifactPanelViewEntry[];
}> = ({ workflowId, runId, nested }) => {
  const artifactPanel = useArtifactPanel();
  const orgSlug = useWhoami((s) => s.org.slug);

  const openRunId = useCallback(
    (nextRunId: string) => {
      const { entry } = resolveArtifactLink(
        { kind: 'workflow-run', workflowId, runId: nextRunId },
        orgSlug
      );
      artifactPanel?.pushView(entry, { replaceTop: true });
    },
    [artifactPanel, orgSlug, workflowId]
  );

  const top = nested[nested.length - 1];
  const stepView =
    top !== undefined && isWorkflowRunStepViewPayload(top.payload)
      ? top.payload
      : null;

  return (
    <WorkflowRunStackContent
      workflowId={workflowId}
      runId={runId}
      onOpenRunId={artifactPanel !== null ? openRunId : undefined}
      stepView={stepView}
    />
  );
};

/**
 * Renders the artifact-link view sitting on the focused artifact's stack. The
 * host artifact stays mounted underneath (hidden) so its editor state and
 * in-scope models survive the round trip.
 *
 * `nested` is whatever the link's own body pushed above it — handed to the
 * destination rather than interpreted here, since only the destination knows
 * its drill-ins.
 */
export const ArtifactLinkStackView: React.FC<{
  link: ArtifactLinkTarget;
  nested: readonly ArtifactPanelViewEntry[];
  onBack: () => void;
}> = ({ link, nested, onBack }) => {
  const orgSlug = useWhoami((s) => s.org.slug);
  const resolved = useMemo(
    () => resolveArtifactLink(link, orgSlug),
    [link, orgSlug]
  );
  const title = resolved.entry.title;

  return (
    <ResourcePageShell
      layout="flat"
      leading={<ResourcePageShellBackButton onClick={onBack} />}
      breadcrumbs={title === undefined ? [] : [{ label: title }]}
    >
      <div className="flex h-full w-full min-h-0 flex-col overflow-hidden">
        {resolved.render(nested)}
      </div>
    </ResourcePageShell>
  );
};
